'use client';

import React, { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { fetchScreens } from 'state/actions/screens';
import { imgUrl } from 'utils/url';
import { CalendarTickIcon, DurationIcon } from 'assets/icon';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OnScreenIcon from '../../assets/icon/calendar/published.png';
import DisplayedIcon from '../../assets/icon/calendar/archived.png';
import ScheduledIcon from '../../assets/icon/calendar/unpublished.png';
import PopUpMediaTimeline from './PopUpMediaTimeline';

dayjs.extend(utc);
dayjs.extend(timezone);

const SGT_TZ = 'Asia/Singapore';

const is1920 = window.innerWidth >= 1920;

const SCALE = is1920 ? 2 : 1;
const CARD_PADDING_X = 12;
const CELL_WIDTH = 225 * SCALE;
const HOUR_WIDTH = CELL_WIDTH;
const MINUTE_WIDTH = HOUR_WIDTH / 60;
const ROW_PADDING = 30 * SCALE;
const CARD_GAP = 10 * SCALE;
const START_TOP = 10 * SCALE;
const CARD_HEIGHT = 36 * SCALE;
const THUMB_SIZE = 28 * SCALE;
const MENU_SIZE = 28 * SCALE;

const VIEW_MODES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

const MAX_VISIBLE_MEDIA = 4;

const TimelineWrapper = ({
  groupedPlaylists,
  searchTimeline,
  selectedDate,
  viewMode,
  expandedPlaylist,
  setExpandedPlaylist,
  siteId,
  isDailyHours,
}) => {
  const [popup, setPopup] = useState({
    open: false,
    media: null,
    top: 0,
    left: 0,
  });

  const menuRefs = useRef({});
  const dispatch = useDispatch();
  const { screensList } = useSelector(
    (state) => ({
      screensList: state.screens.list,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!siteId) return;

    dispatch(fetchScreens({ siteId }));
  }, [dispatch, siteId]);

  const [menuOpen, setMenuOpen] = useState(null);
  const wrapperRef = useRef(null);
  const toSGT = (date) => dayjs.utc(date).tz(SGT_TZ);

  /* =========================
     TIMELINE RANGE
  ========================= */

  const timelineRange = useMemo(() => {
    const current = toSGT(selectedDate);

    switch (viewMode) {
      case VIEW_MODES.DAILY: {
        const start = current.startOf('day').add(1, 'hour');

        return {
          start,
          end: start.add(23, 'hour').endOf('hour'),
          columns: isDailyHours ? 6 : 24,
        };
      }

      case VIEW_MODES.WEEKLY: {
        const start = current.startOf('day');

        return {
          start,
          end: start.add(6, 'day').endOf('day'),
          columns: 7,
        };
      }

      case VIEW_MODES.MONTHLY: {
        const monthStart = current.startOf('month');
        const monthEnd = current.endOf('month');

        const weeks = [];

        let cursor = monthStart.startOf('week').add(1, 'day');

        while (cursor.isBefore(monthEnd) || cursor.isSame(monthEnd, 'day')) {
          weeks.push({
            start: cursor,
            end: cursor.add(6, 'day'),
          });

          cursor = cursor.add(7, 'day');
        }

        return {
          start: monthStart,
          end: monthEnd,
          weeks,
          columns: weeks.length,
        };
      }

      case VIEW_MODES.YEARLY:
        return {
          start: current.startOf('year'),
          end: current.endOf('year'),
          columns: 12,
        };

      default:
        return {
          start: current.startOf('day'),
          end: current.endOf('day'),
          columns: 24,
        };
    }
  }, [selectedDate, viewMode, isDailyHours]);
  /* =========================
   MEDIA START / END
========================= */

  const getMediaStart = (media) => {
    const start =
      media.type === 'all' ? dayjs(media.calendarDate) : toSGT(media.start);

    return start;
  };

  const getMediaEnd = (media) => {
    if (media.type === 'all') {
      return dayjs(media.calendarDate).endOf('day');
    }

    return toSGT(media.end);
  };
  /* =========================
     HEADER
  ========================= */

  const columns = useMemo(() => {
    switch (viewMode) {
      case VIEW_MODES.DAILY: {
        if (!isDailyHours) {
          return Array.from(
            { length: 24 },
            (_, i) => `${String((i + 1) % 24).padStart(2, '0')}:00`
          );
        }
        console.log('isDailyHours header', isDailyHours);
        return [
          '01:00 - 04:00',
          '04:00 - 08:00',
          '08:00 - 12:00',
          '12:00 - 16:00',
          '16:00 - 20:00',
          '20:00 - 24:00',
        ];
      }

      case VIEW_MODES.WEEKLY:
        return Array.from({ length: 7 }, (_, i) =>
          timelineRange.start.add(i, 'day').format('DD MMM')
        );

      case VIEW_MODES.MONTHLY:
        return timelineRange.weeks.map(
          (week) =>
            `${week.start.format('DD MMM')} - ${week.end.format('DD MMM')}`
        );

      case VIEW_MODES.YEARLY:
        return Array.from({ length: 12 }, (_, i) =>
          dayjs().month(i).format('MMM').toUpperCase()
        );

      default:
        return [];
    }
  }, [viewMode, timelineRange]);

  /* =========================
     TIMELINE WIDTH
  ========================= */

  const timelineWidth = useMemo(() => {
    return timelineRange.columns * CELL_WIDTH;
  }, [timelineRange]);

  /* =========================
     POSITION
  ========================= */

  const getLeft = (media) => {
    let start = getMediaStart(media);

    if (start.isBefore(timelineRange.start)) {
      start = timelineRange.start;
    }

    if (start.isAfter(timelineRange.end)) {
      start = timelineRange.end;
    }

    switch (viewMode) {
      case VIEW_MODES.DAILY: {
        if (!isDailyHours) {
          return start.diff(timelineRange.start, 'minute') * MINUTE_WIDTH;
        }
        const hours = start.diff(timelineRange.start, 'hour', true);

        return (hours / 4) * CELL_WIDTH;
      }

      case VIEW_MODES.WEEKLY: {
        const day = start.startOf('day');

        return day.diff(timelineRange.start.startOf('day'), 'day') * CELL_WIDTH;
      }

      case VIEW_MODES.MONTHLY: {
        const days = start.diff(timelineRange.start, 'day');
        return Math.floor(days / 7) * HOUR_WIDTH;
      }

      case VIEW_MODES.YEARLY:
        return start.diff(timelineRange.start, 'month') * HOUR_WIDTH;

      default:
        return 0;
    }
  };

  /* =========================
     WIDTH
  ========================= */

  const getWidth = (media) => {
    let start = getMediaStart(media);
    let end = getMediaEnd(media);

    // Clamp agar tidak keluar timeline
    if (start.isBefore(timelineRange.start)) {
      start = timelineRange.start;
    }

    if (end.isAfter(timelineRange.end)) {
      end = timelineRange.end;
    }

    switch (viewMode) {
      case VIEW_MODES.DAILY: {
        if (!isDailyHours) {
          const minutes = end.diff(start, 'minute', true);

          return Math.max(minutes * MINUTE_WIDTH, 40);
        }

        const hours = end.diff(start, 'hour', true);

        return Math.max((hours / 4) * CELL_WIDTH, 40);
      }

      case VIEW_MODES.WEEKLY: {
        const startDay = start.startOf('day');
        const endDay = end.startOf('day');

        const days = endDay.diff(startDay, 'day') + 1;

        return days * CELL_WIDTH;
      }

      case VIEW_MODES.MONTHLY: {
        const startWeek = Math.floor((start.date() - 1) / 7);
        const endWeek = Math.floor((end.date() - 1) / 7);

        return Math.max((endWeek - startWeek + 1) * HOUR_WIDTH, 40);
      }

      case VIEW_MODES.YEARLY: {
        const startMonth = start.startOf('month');
        const endMonth = end.startOf('month');

        const columns = endMonth.diff(startMonth, 'month') + 1;

        return Math.max(columns * CELL_WIDTH, 40);
      }

      default:
        return 40;
    }
  };

  useEffect(() => {
    if (viewMode !== VIEW_MODES.DAILY) return;
    if (!wrapperRef.current) return;

    const scrollLeft = isDailyHours ? 0 : (8 - 1) * HOUR_WIDTH;

    wrapperRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, [selectedDate, viewMode, isDailyHours]);

  const getMediaStatus = (media) => {
    const now = dayjs();

    const start = dayjs.utc(media.start).tz('Asia/Singapore');
    const end = dayjs.utc(media.end).tz('Asia/Singapore');

    if (now.isBefore(start)) {
      return 'scheduled';
    }

    if (now.isSame(start) || (now.isAfter(start) && now.isBefore(end))) {
      return 'on-screen';
    }

    return 'displayed';
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.media-menu-wrapper')) {
        setMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  useEffect(() => {
    const close = (e) => {
      const popupElement = document.querySelector('.media-popup');

      if (
        popupElement &&
        !popupElement.contains(e.target) &&
        !e.target.closest('.media-menu')
      ) {
        setPopup((prev) => ({
          ...prev,
          open: false,
        }));
      }
    };

    document.addEventListener('mousedown', close);

    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div ref={wrapperRef} className="timeline-wrapper">
      <div className="timeline-content">
        {/* HEADER */}
        <div className="timeline-header">
          <div className="playlist-column-header">Playlist</div>

          <div className="hours-row" style={{ width: `${timelineWidth}px` }}>
            {columns.map((column) => (
              <div
                key={column}
                className="hour-cell"
                style={{
                  minWidth: `${CELL_WIDTH}px`,
                  width: `${CELL_WIDTH}px`,
                }}
              >
                {column}
              </div>
            ))}
          </div>
        </div>

        {/* BODY */}
        <div className="timeline-body">
          {groupedPlaylists.map((playlist, playlistIndex) => {
            const hasSearch = !!searchTimeline?.trim();

            const isExpanded = hasSearch
              ? playlist.hasMatched
              : expandedPlaylist === playlist.id;

            const mediaToRender = isExpanded
              ? playlist.medias
              : playlist.medias.slice(0, MAX_VISIBLE_MEDIA);

            const hiddenCount = Math.max(
              playlist.medias.length - MAX_VISIBLE_MEDIA,
              0
            );

            const rowHeight =
              START_TOP +
              mediaToRender.length * (CARD_HEIGHT + CARD_GAP) +
              ROW_PADDING;

            const bg = playlistIndex % 2 === 0 ? '#fff' : '#f8fafc';

            const uniqueScreenIds = [
              ...new Set(
                playlist.medias.flatMap((media) => media.screenIds || [])
              ),
            ];

            const screenMap = new Map(
              screensList.map((screen) => [screen._id, screen.name])
            );

            const screenNames = uniqueScreenIds
              .map((id) => screenMap.get(id))
              .filter(Boolean);

            return (
              <div
                key={playlist.id}
                className="timeline-row"
                style={{ minHeight: `${rowHeight}px` }}
              >
                {/* LEFT */}
                <div className="playlist-info" style={{ background: bg }}>
                  <h4>{playlist.name}</h4>

                  <div className="screens">
                    {screenNames.map((name, index) => (
                      <React.Fragment key={name}>
                        {index > 0 && <span className="separator"> • </span>}
                        <span>{name}</span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* GRID */}
                <div className="timeline-scroll" style={{ background: bg }}>
                  {mediaToRender.length > 0 && (
                    <div
                      className="timeline-grid"
                      style={{
                        width: timelineWidth,
                        height: rowHeight,
                        backgroundSize: `${CELL_WIDTH}px 100%`,
                        backgroundImage: `
                          repeating-linear-gradient(
                            to right,
                            transparent,
                            transparent ${CELL_WIDTH - 1}px,
                            #e5e7eb ${CELL_WIDTH - 1}px,
                            #e5e7eb ${CELL_WIDTH}px
                          )
                        `,
                      }}
                    >
                      {/* MEDIA */}
                      {mediaToRender.map((media, index) => {
                        const top =
                          START_TOP + index * (CARD_HEIGHT + CARD_GAP);

                        const left = getLeft(media) + CARD_PADDING_X;

                        const width = Math.max(
                          getWidth(media) - CARD_PADDING_X * 2,
                          40
                        );
                        const status = getMediaStatus(media);
                        const menuKey = `${media._id}-${media.start}-${media.end}-${media.index}`;

                        const renderMedia = () => {
                          if (media.logo && media.fileType) {
                            const fileType = media.fileType.split('/')[0];
                            if (fileType === 'image') {
                              return (
                                <img
                                  src={`${imgUrl}/media/${media.logo}`}
                                  alt="default"
                                  draggable={false}
                                />
                              );
                            }
                            if (fileType === 'video') {
                              return (
                                <video
                                  id="my-video"
                                  className="video-js card-list-video"
                                  data-setup="{}"
                                  preload="metadata"
                                >
                                  <source
                                    src={`${imgUrl}/media/${media.logo}`}
                                    type="video/mp4"
                                  />
                                  <track kind="captions" />
                                </video>
                              );
                            }
                          }
                          return null;
                        };
                        return (
                          <div
                            className={`
                          media-card
                          ${
                            searchTimeline?.trim() && media.isMatched
                              ? 'media-card-active'
                              : ''
                          }
                          ${
                            searchTimeline?.trim() && !media.isMatched
                              ? 'media-card-dim'
                              : ''
                          }
                        `}
                            style={{
                              position: 'absolute',
                              left,
                              top,
                              width,

                              height: CARD_HEIGHT,
                              minHeight: CARD_HEIGHT,
                              maxHeight: CARD_HEIGHT,
                            }}
                          >
                            <div className="media-card-left">
                              <div
                                className="media-thumbnail"
                                style={{
                                  width: THUMB_SIZE,
                                  height: THUMB_SIZE,
                                }}
                              >
                                {media.logo ? (
                                  renderMedia()
                                ) : (
                                  <i className="feather-image" />
                                )}
                              </div>

                              <div className="timeline-media-content">
                                <h5>{media.name}</h5>

                                <div className="media-detail">
                                  <CalendarTickIcon
                                    style={{
                                      width: 8,
                                      height: 8,
                                      flexShrink: 0,
                                    }}
                                  />

                                  <span>
                                    {dayjs
                                      .utc(media.start)
                                      .tz('Asia/Singapore')
                                      .format('D/M/YY HH:mm')}
                                    {' - '}
                                    {dayjs
                                      .utc(media.end)
                                      .tz('Asia/Singapore')
                                      .format('D/M/YY HH:mm')}
                                  </span>
                                </div>

                                <div className="media-detail">
                                  <DurationIcon
                                    style={{
                                      width: 8,
                                      height: 8,
                                      flexShrink: 0,
                                    }}
                                  />

                                  <span>{media.duration}s</span>
                                </div>
                              </div>
                            </div>
                            <div className="media-card-right">
                              {status === 'on-screen' && (
                                <div className="media-screen-active">
                                  <p>On Screen</p>
                                  <img src={OnScreenIcon} alt="screen active" />
                                </div>
                              )}
                              {status === 'scheduled' && (
                                <div className="media-screen-active">
                                  <p>Scheduled</p>
                                  <img
                                    src={ScheduledIcon}
                                    alt="screen active"
                                  />
                                </div>
                              )}
                              {status === 'displayed' && (
                                <div className="media-screen-active">
                                  <p>Displayed</p>
                                  <img
                                    src={DisplayedIcon}
                                    alt="screen active"
                                  />
                                </div>
                              )}
                              <div className="media-menu-wrapper">
                                <button
                                  ref={(el) => (menuRefs.current[menuKey] = el)}
                                  style={{
                                    width: MENU_SIZE,
                                    height: MENU_SIZE,
                                    flex: `0 0 ${MENU_SIZE}px`,
                                  }}
                                  className="media-menu"
                                  onClick={() => {
                                    const rect =
                                      menuRefs.current[
                                        menuKey
                                      ].getBoundingClientRect();

                                    setPopup((prev) => ({
                                      open:
                                        !prev.open ||
                                        prev.media?._id !== media._id,
                                      media,
                                      top: rect.bottom - 30 * SCALE,
                                      left: rect.right + 0,
                                    }));
                                  }}
                                >
                                  <i className="feather-more-vertical" />
                                </button>
                                {/* {menuOpen === menuKey && (
                                  <PopUpMediaTimeline media={media} />
                                )} */}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* MORE */}
                      {hiddenCount > 0 && !hasSearch && (
                        <div
                          className="media-more"
                          style={{
                            top:
                              START_TOP +
                              mediaToRender.length * (CARD_HEIGHT + CARD_GAP),
                          }}
                          onClick={() =>
                            setExpandedPlaylist(isExpanded ? null : playlist.id)
                          }
                        >
                          {isExpanded ? 'Show Less' : `+${hiddenCount} More`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {popup.open &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: popup.top,
              left: popup.left,
              zIndex: 999999,
            }}
          >
            <PopUpMediaTimeline media={popup.media} />
          </div>,
          document.body
        )}
    </div>
  );
};

export default TimelineWrapper;
